// App Editor - Dialog for configuring app settings and fields
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Plus, Trash2, GripVertical, ChevronUp, ChevronDown } from 'lucide-react';
import { FIELD_TYPE_OPTIONS, FIELD_TYPES } from './appConstants';

export default function AppEditor({ 
  app, 
  onSave, 
  onClose,
  isOpen 
}) {
  const [config, setConfig] = useState(null);
  const [editingField, setEditingField] = useState(null);

  useEffect(() => {
    if (app) {
      setConfig({
        ...app,
        fields: app.fields || [],
        button_text: app.button_text || app.buttonText || 'Submit',
        button_icon: app.button_icon || app.icon || '📩',
        title: app.title || '',
        description: app.description || '',
        fund_summary: app.fund_summary || '',
        fund_points: app.fund_points || []
      });
    }
  }, [app]);

  if (!config) return null;

  const updateField = (fieldId, updates) => {
    setConfig(prev => ({
      ...prev,
      fields: prev.fields.map(f => f.id === fieldId ? { ...f, ...updates } : f)
    }));
  };

  const addField = (type) => {
    const newField = {
      id: `field_${crypto.randomUUID()}`,
      type,
      label: '',
      placeholder: '',
      required: false,
      options: type === FIELD_TYPES.DROPDOWN ? ['Option 1', 'Option 2'] : []
    };
    setConfig(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
    setEditingField(newField.id);
  };

  const removeField = (fieldId) => {
    setConfig(prev => ({
      ...prev,
      fields: prev.fields.filter(f => f.id !== fieldId)
    }));
  };

  const moveField = (fieldId, direction) => {
    const fields = [...config.fields];
    const idx = fields.findIndex(f => f.id === fieldId);
    if (direction === 'up' && idx > 0) {
      [fields[idx], fields[idx - 1]] = [fields[idx - 1], fields[idx]];
    } else if (direction === 'down' && idx < fields.length - 1) {
      [fields[idx], fields[idx + 1]] = [fields[idx + 1], fields[idx]];
    }
    setConfig(prev => ({ ...prev, fields }));
  };

  const handleSave = () => {
    onSave({
      ...config,
      enabled: true
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-xl">{config.button_icon}</span>
            Configure {app?.name || 'App'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Basic Settings */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700">Button Settings</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">Button Text</label>
                <Input
                  value={config.button_text}
                  onChange={(e) => setConfig(prev => ({ ...prev, button_text: e.target.value }))}
                  placeholder="Pitch to Me"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">Button Icon</label>
                <Input
                  value={config.button_icon}
                  onChange={(e) => setConfig(prev => ({ ...prev, button_icon: e.target.value }))}
                  placeholder="📩"
                  className="font-mono"
                />
              </div>
            </div>
          </div>

          {/* Form Header */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700">Form Header (Optional)</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">Title</label>
                <Input
                  value={config.title}
                  onChange={(e) => setConfig(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Submit Your Pitch"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">Description</label>
                <Textarea
                  value={config.description}
                  onChange={(e) => setConfig(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Tell me about your startup and I'll get back to you..."
                  rows={2}
                />
              </div>
            </div>
          </div>

          {/* Fund Summary */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700">Fund Summary (Shows at top of form)</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">Summary Text</label>
                <Textarea
                  value={config.fund_summary || ''}
                  onChange={(e) => setConfig(prev => ({ ...prev, fund_summary: e.target.value }))}
                  placeholder="We invest in early-stage B2B SaaS companies..."
                  rows={2}
                  className="text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">Key Points</label>
                <div className="space-y-2">
                  {(config.fund_points || []).map((point, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="text-indigo-500 text-sm">•</span>
                      <Input
                        value={point}
                        onChange={(e) => {
                          const newPoints = [...(config.fund_points || [])];
                          newPoints[idx] = e.target.value;
                          setConfig(prev => ({ ...prev, fund_points: newPoints }));
                        }}
                        placeholder="e.g., Check size: $500K - $2M"
                        className="text-sm"
                      />
                      <button
                        onClick={() => {
                          const newPoints = (config.fund_points || []).filter((_, i) => i !== idx);
                          setConfig(prev => ({ ...prev, fund_points: newPoints }));
                        }}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => setConfig(prev => ({ 
                      ...prev, 
                      fund_points: [...(prev.fund_points || []), ''] 
                    }))}
                    className="flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-700 font-medium mt-1"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add point
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Form Fields */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700">Form Fields</h3>
              <span className="text-xs text-gray-400">{config.fields.length} fields</span>
            </div>

            <div className="space-y-2">
              {config.fields.map((field, idx) => (
                <div 
                  key={field.id}
                  className={`border rounded-lg p-3 ${editingField === field.id ? 'border-indigo-300 bg-indigo-50/50' : 'border-gray-200 bg-white'}`}
                >
                  <div className="flex items-center gap-2">
                    {/* Drag handle (visual only for now) */}
                    <GripVertical className="w-4 h-4 text-gray-300" />
                    
                    {/* Field type icon */}
                    <span className="text-sm">
                      {FIELD_TYPE_OPTIONS.find(t => t.value === field.type)?.icon || '📝'}
                    </span>

                    {/* Field label */}
                    <div className="flex-1 min-w-0">
                      {editingField === field.id ? (
                        <Input
                          value={field.label}
                          onChange={(e) => updateField(field.id, { label: e.target.value })}
                          placeholder="Field label"
                          className="h-8 text-sm"
                          autoFocus
                        />
                      ) : (
                        <span 
                          className="text-sm font-medium text-gray-700 cursor-pointer hover:text-indigo-600"
                          onClick={() => setEditingField(field.id)}
                        >
                          {field.label || <span className="text-gray-400 italic">Untitled field</span>}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => moveField(field.id, 'up')}
                        disabled={idx === 0}
                        className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => moveField(field.id, 'down')}
                        disabled={idx === config.fields.length - 1}
                        className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => removeField(field.id)}
                        className="p-1 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Expanded editing */}
                  {editingField === field.id && (
                    <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-gray-500 block mb-1">Placeholder</label>
                          <Input
                            value={field.placeholder}
                            onChange={(e) => updateField(field.id, { placeholder: e.target.value })}
                            placeholder="Placeholder text"
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 block mb-1">Type</label>
                          <select
                            value={field.type}
                            onChange={(e) => updateField(field.id, { type: e.target.value })}
                            className="w-full h-8 text-sm border rounded-md px-2"
                          >
                            {FIELD_TYPE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.icon} {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Dropdown options */}
                      {field.type === FIELD_TYPES.DROPDOWN && (
                        <div>
                          <label className="text-xs text-gray-500 block mb-1">Options (one per line)</label>
                          <Textarea
                            value={(field.options || []).join('\n')}
                            onChange={(e) => updateField(field.id, { 
                              options: e.target.value.split('\n').filter(o => o.trim()) 
                            })}
                            placeholder="Option 1&#10;Option 2&#10;Option 3"
                            rows={3}
                            className="text-sm font-mono"
                          />
                        </div>
                      )}

                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={field.required}
                          onChange={(e) => updateField(field.id, { required: e.target.checked })}
                          className="rounded border-gray-300"
                        />
                        Required field
                      </label>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingField(null)}
                        className="text-xs"
                      >
                        Done editing
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Add field buttons */}
            <div className="flex flex-wrap gap-2 pt-2">
              {FIELD_TYPE_OPTIONS.map(opt => (
                <Button
                  key={opt.value}
                  variant="outline"
                  size="sm"
                  onClick={() => addField(opt.value)}
                  className="text-xs"
                >
                  {opt.icon} Add {opt.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} className="bg-indigo-600 hover:bg-indigo-700">
              Save App
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
